import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { TagsWithPopover } from "kontrolEdge/components/Shared/TagsWithPopover/TagsWithPopover";
import type { Tag, TagAssignment } from "./types";
import { lookupServiceType } from "./requests/productAdapters";
import { TAGS_MANAGER_NO_CATEGORY_SENTINEL } from "@redux/slices/tagsManagerFiltersSlice";

export type { Tag } from "./types";

const formatDate = (iso: string): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/**
 * Row enriched with derived fields (computed from the assignments map at
 * render time so they stay in sync with CRUD operations).
 */
export interface TagRow extends Tag {
  resourceCount: number;
  productSpread: string[];
}

export const computeProductSpread = (
  assignments: TagAssignment[] | undefined
): string[] => {
  if (!assignments || assignments.length === 0) return [];
  const set = new Set<string>();
  for (const a of assignments) {
    const entry = lookupServiceType(a.serviceType);
    if (entry) set.add(entry.product.productLabel);
  }
  return Array.from(set).sort();
};

/* ------------------------------------------------------------------ */
/* Global multi-filter predicates (Backbone-style: empty = "show all", */
/*  AND across fields, OR within a field's selection).                 */
/* ------------------------------------------------------------------ */

export function matchesTagSelection(
  row: TagRow,
  selection: string[]
): boolean {
  if (selection.length === 0) return true;
  return selection.includes(row.id);
}

export function matchesCategorySelection(
  row: TagRow,
  selection: string[]
): boolean {
  if (selection.length === 0) return true;
  if (row.category) return selection.includes(row.category.name);
  return selection.includes(TAGS_MANAGER_NO_CATEGORY_SENTINEL);
}

/**
 * Product filter matches if ANY of the products the tag spans is in the
 * selection. A tag with no resources attached has empty productSpread →
 * doesn't match any product filter (correct behavior: filtering by
 * product implicitly excludes "unused" tags).
 */
export function matchesProductSelection(
  row: TagRow,
  selection: string[]
): boolean {
  if (selection.length === 0) return true;
  return row.productSpread.some((p) => selection.includes(p));
}

/* ------------------------------------------------------------------ */
/* Column widths                                                       */
/*                                                                     */
/* Sized so the whole row fits on common desktop viewports without     */
/* horizontal scroll on the table itself. Only the Description column  */
/* is constrained-with-internal-overflow: when the text exceeds        */
/* `width`, the cell shows a thin horizontal scrollbar (see            */
/* `custom-scrollbar` in `src/index.css`). Every other column either   */
/* truncates (long strings) or has predictable short content (counts,  */
/* date, pill). The Actions column lives in `TagsManagerPage.tsx`      */
/* (override) with a 100px width and intentionally NOT `fixed`.        */
/*                                                                     */
/* Approximate row width with all columns visible:                     */
/*   Tag(200) + Description(250) + Category(130) + Resources(100)      */
/*   + Used in(170) + Last updated(130) + Actions(100) = ~1080px       */
/*                                                                     */
/* Partner view adds the Customer column (160px), total ~1240px,       */
/* which still fits inside the standard sidebar-less content area.     */
/* ------------------------------------------------------------------ */

export const tagsColumns: GenericTableColumn<TagRow>[] = [
  {
    header: "Tag",
    accessor: "name",
    width: "200px",
    wrapMode: "truncate",
    sortAccessor: (row) => row.name.toLowerCase(),
    renderCell: (row) => (
      <div className="flex items-center gap-2 min-w-0 w-full">
        <span
          aria-hidden
          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{
            background: row.color ?? "var(--color-primitive-gray-300)",
          }}
        />
        <span className="font-medium text-semantic-text-primary truncate min-w-0">
          {row.name}
        </span>
      </div>
    ),
  },
  {
    header: "Description",
    accessor: "description",
    width: "250px",
    wrapMode: "nowrap",
    renderCell: (row) =>
      row.description ? (
        <div className="overflow-x-auto custom-scrollbar max-w-full">
          <span className="text-semantic-text-secondary inline-block whitespace-nowrap">
            {row.description}
          </span>
        </div>
      ) : (
        <span className="italic text-semantic-text-secondary">—</span>
      ),
  },
  {
    header: "Category",
    accessor: "category",
    width: "130px",
    wrapMode: "truncate",
    renderCell: (row) =>
      row.category ? (
        <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-semantic-bg-secondary text-semantic-text-primary truncate max-w-full">
          {row.category.name}
        </span>
      ) : (
        <span className="text-semantic-text-secondary">—</span>
      ),
  },
  {
    header: "Resources",
    width: "100px",
    sortAccessor: (row) => row.resourceCount,
    renderCell: (row) => (
      <span className="text-semantic-text-primary font-medium">
        {row.resourceCount}
      </span>
    ),
  },
  {
    header: "Used in",
    sortAccessor: (row) => row.productSpread.join(","),
    width: "170px",
    wrapMode: "nowrap",
    renderCell: (row) =>
      row.productSpread.length === 0 ? (
        <span className="text-semantic-text-secondary">—</span>
      ) : (
        <TagsWithPopover
          tags={row.productSpread}
          maxVisible={1}
          triggerSingleRow
        />
      ),
  },
  {
    header: "Last updated",
    width: "130px",
    wrapMode: "nowrap",
    sortAccessor: (row) => new Date(row.updatedAt).getTime(),
    renderCell: (row) => (
      <span className="text-semantic-text-secondary text-sm">
        {formatDate(row.updatedAt)}
      </span>
    ),
  },
];
